import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, X } from "lucide-react";
import { KindredMark } from "./KindredMark";

const COLORS = ["#C9A227", "#D8B23C", "#F5D26A", "#F7F4ED"];
const LIFETIME_MS = 3600;
const SOUND_PREF_KEY = "ch_celebration_sound";

const spawnBurst = (particles, cx, cy, count, speed) => {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const v = (0.3 + Math.random() * 0.7) * speed;
    particles.push({
      x: cx,
      y: cy,
      vx: Math.cos(angle) * v,
      vy: Math.sin(angle) * v - speed * 0.35,
      size: 1.5 + Math.random() * 3.5,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      star: Math.random() < 0.4,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.15,
      life: 0,
      ttl: 130 + Math.random() * 110,
    });
  }
};

const drawStar = (ctx, x, y, r, rot) => {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const rad = i % 2 === 0 ? r : r * 0.32;
    const a = rot + (i * Math.PI) / 4;
    const px = x + Math.cos(a) * rad;
    const py = y + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
};

export const CelebrationOverlay = ({ city, onDone }) => {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const [soundOn, setSoundOn] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(SOUND_PREF_KEY) !== "off"
  );

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (soundOn && !reduced) {
      const audio = new Audio("/celebration-chime.mp3");
      audio.volume = 0.55;
      audioRef.current = audio;
      audio.play().catch(() => {});
    }
    const timer = setTimeout(() => doneRef.current(), reduced ? 2400 : LIFETIME_MS);
    if (reduced) return () => { clearTimeout(timer); audioRef.current?.pause(); };

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = [];
    const W = () => canvas.width / dpr;
    const H = () => canvas.height / dpr;
    // grand center burst, then two smaller side bursts
    spawnBurst(particles, W() / 2, H() * 0.42, 110, 9);
    const t1 = setTimeout(() => spawnBurst(particles, W() * 0.3, H() * 0.5, 45, 6.5), 350);
    const t2 = setTimeout(() => spawnBurst(particles, W() * 0.7, H() * 0.5, 45, 6.5), 550);

    let raf;
    const tick = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W(), H());
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += 1;
        if (p.life > p.ttl) {
          particles.splice(i, 1);
          continue;
        }
        p.vy += 0.055; // gravity
        p.vx *= 0.986; // drag
        p.vy *= 0.992;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        const fade = 1 - p.life / p.ttl;
        const twinkle = 0.65 + 0.35 * Math.sin(p.life * 0.35 + p.rot * 5);
        ctx.globalAlpha = Math.max(0, fade * twinkle);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        if (p.star) {
          drawStar(ctx, p.x, p.y, p.size * 2.1, p.rot);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", resize);
      audioRef.current?.pause();
    };
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    window.localStorage.setItem(SOUND_PREF_KEY, next ? "on" : "off");
    if (!next) {
      audioRef.current?.pause();
    } else if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } else {
      const audio = new Audio("/celebration-chime.mp3");
      audio.volume = 0.55;
      audioRef.current = audio;
      audio.play().catch(() => {});
    }
  };

  const cityLine =
    city && city !== "Not specified" && city !== "Somewhere else"
      ? `We'll notify you when Community Hearts launches in ${city}.`
      : "We'll notify you when Community Hearts launches in your city.";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0E1A2E]/[0.97] backdrop-blur-sm"
      data-testid="celebration-overlay"
    >
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />

      <button
        onClick={() => doneRef.current()}
        aria-label="Close"
        data-testid="celebration-close"
        className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold active:scale-95"
      >
        <X className="h-5 w-5" />
      </button>

      <button
        onClick={toggleSound}
        aria-label={soundOn ? "Mute celebration sound" : "Unmute celebration sound"}
        data-testid="celebration-sound-toggle"
        className="absolute right-20 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold active:scale-95"
      >
        {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
      </button>

      <div className="relative px-6 text-center">
        <motion.div
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-gold/40 bg-gold/10"
        >
          <KindredMark className="h-12 w-12" />
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 font-serif text-5xl tracking-tight text-cream md:text-7xl"
          data-testid="celebration-headline"
        >
          You're <span className="italic text-gold">in.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-cream/70"
          data-testid="celebration-subline"
        >
          {cityLine}
        </motion.p>
      </div>
    </motion.div>
  );
};
